import {
  ChangeEvent,
  Fragment,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { ReactComponent as TickIcon } from "../../images/tick.svg";
import { ReactComponent as WarnIcon } from "../../images/warn.svg";
import { isValueNumeric } from "../../utils";
import { numberToWord } from "../../utils/number-to-word";
import Alert from "../alert";
import { HorizontalSelect, InputField } from "../input-functions";
import Modal from "../modal";

export type ConfirmDialogType = "success" | "reject";

interface ConfirmDialogInputField
  extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  type?: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  numberToWordRequired?: boolean;
}

interface ConfirmDialogSelectField {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
}

interface ConfirmDialogProps {
  active: boolean;
  type?: ConfirmDialogType;
  title: string;
  message: string | ReactNode;
  afterSuccessMessage?: string;
  customErrorMessage?: string;
  onSubmit: (
    type: ConfirmDialogType,
    fieldData: { [x: string]: string }
  ) => Promise<any>;
  onCancel: () => void;
  submitButtonText?: string;
  cancelButtonText?: string;
  primaryActionButton?: "submit" | "cancel";
  needInputFields?: boolean;
  inputFields?: ConfirmDialogInputField[];
  selectFields?: ConfirmDialogSelectField[];
}

type StatusType = "INITIATED" | "SUCCESS";

export default function ConfirmDialog({
  active = false,
  type = "success",
  title,
  message,
  afterSuccessMessage,
  customErrorMessage,
  onSubmit,
  onCancel,
  submitButtonText = "Yes",
  cancelButtonText = "No",
  primaryActionButton = "submit",
  inputFields = [],
  selectFields = [],
}: ConfirmDialogProps) {
  const initalFieldState = (): { [x: string]: string } =>
    [...inputFields, ...selectFields].reduce((prevObj, currObj) => {
      const value =
        "options" in currObj && currObj.options.length > 0
          ? currObj.options[0].value // Default to the first option if available
          : "value" in currObj
          ? currObj.value || ""
          : "";
      return Object.assign(prevObj, { [currObj.name]: value });
    }, {});

  const [status, setStatus] = useState<StatusType>("INITIATED");
  const [isLoading, setIsLoading] = useState(false);
  const [fieldData, setFieldData] = useState(initalFieldState);
  const [successMessage, setSuccessMessage] = useState(afterSuccessMessage);
  const [errorMessage, setErrorMessage] = useState(customErrorMessage);

  useEffect(() => {
    if (active) {
      setStatus("INITIATED");
      setFieldData(initalFieldState);
      setErrorMessage(undefined);
    }
  }, [active]);

  function getRequiredFieldError(): string | undefined {
    const requiredField = [...inputFields, ...selectFields].find(
      (field) => field.required && !fieldData[field.name]?.trim()
    );
    return requiredField ? `${requiredField.label} is required` : undefined;
  }

  async function handleSubmit() {
    const requiredFieldError = getRequiredFieldError();
    if (requiredFieldError) {
      setErrorMessage(requiredFieldError);
      return;
    }

    setStatus("INITIATED");
    setIsLoading(true);
    setErrorMessage(undefined);
    try {
      const response = await onSubmit(type, fieldData);
      console.log(response);
      if (response?.message) {
        setSuccessMessage(response.message);
      }
      setStatus("SUCCESS");
    } catch (error) {
      setErrorMessage(error.message || "Something went wrong. Try again later");
    } finally {
      setIsLoading(false);
    }
  }

  function handleInputFieldChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    const inputMode = e.target.inputMode;
    if (inputMode === "numeric" && Number.isNaN(Number(value))) return;
    setFieldData((prevState) => ({
      ...prevState,
      [e.target.name]: value,
    }));
  }

  function handleSelectChange(e: ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;
    setFieldData((prevState) => ({
      ...prevState,
      [e.target.name]: value,
    }));
  }

  function renderNumberToWordIfRequired(
    required: boolean,
    value: unknown
  ): ReactNode {
    if (!required) return null;
    const isNumber = isValueNumeric(value);
    if (!isNumber) return null;
    const numberInWord = numberToWord(value);
    if (numberInWord === null) return null;
    return <span>{numberToWord(value)}</span>;
  }

  function renderInputFields(fields: ConfirmDialogInputField[]) {
    return fields.map((eachField, index) => (
      <Fragment key={index}>
        <InputField
          {...eachField}
          value={fieldData[eachField.name]}
          onChange={handleInputFieldChange}
        />
        {renderNumberToWordIfRequired(
          !!eachField.numberToWordRequired,
          fieldData[eachField.name]
        )}
      </Fragment>
    ));
  }

  function SelectFields({ fields }: { fields: ConfirmDialogSelectField[] }) {
    return (
      <>
        {fields.map((eachField, index) => (
          <Fragment key={index}>
            <HorizontalSelect
              {...eachField}
              value={fieldData[eachField.name]}
              onChange={handleSelectChange}
            />
          </Fragment>
        ))}
      </>
    );
  }

  function renderScreen(status: StatusType) {
    if (status === "SUCCESS")
      return (
        <div className="d-flex flex-column align-items-center">
          {type === "success" && <TickIcon color="#5BC97C" />}
          {type === "reject" && <WarnIcon />}
          <p style={{ marginTop: "20px", marginBottom: "0" }}>
            {successMessage}
          </p>
        </div>
      );
    return (
      <>
        <Alert
          type="danger"
          message={errorMessage}
          canDismiss
          onDismissClick={() => setErrorMessage(undefined)}
        />
        {message}
        {renderInputFields(inputFields)}
        <SelectFields fields={selectFields} />
      </>
    );
  }

  return (
    <Modal
      open={active}
      onSubmit={primaryActionButton === "submit" ? handleSubmit : onCancel}
      onClose={onCancel}
      onCancel={
        status === "INITIATED" && primaryActionButton === "cancel"
          ? handleSubmit
          : onCancel
      }
      title={status === "INITIATED" ? title : undefined}
      hasCloseIcon={status === "INITIATED"}
      hasSubmitButton={status === "INITIATED"}
      submitButtonText={submitButtonText}
      cancelButtonText={status === "INITIATED" ? cancelButtonText : "Ok"}
      hasSubmitButtonLoading={primaryActionButton === "submit" && isLoading}
      isCancelButtonLoading={primaryActionButton === "cancel" && isLoading}
      description="Confirmation dialog box"
    >
      {renderScreen(status)}
    </Modal>
  );
}
